import React from "react";
import { Link } from "react-router-dom";
import { useSidebarState } from "@/hooks/useSidebarState";
import { SidebarToggleButton } from "./SidebarToggleButton";
import { SidebarUserProfile } from "./SidebarUserProfile";
import { SidebarLinks } from "./SidebarLinks.tsx";
import { SidebarDropdown } from "./SidebarDropdown";
import { SidebarFooter } from "./SidebarFooter";
import { getSidebarLinks } from "./sidebarLinks";
import logo from "/lovable-uploads/5639bd1d-d899-4190-af54-f5f73944d997.png";

interface SidebarContainerProps {
  role: string | null;
  nickname: string | null;
}

export const SidebarContainer: React.FC<SidebarContainerProps> = ({ role, nickname }) => {
  const { isCollapsed, toggleSidebar } = useSidebarState();
  const { mainLinks, roleSpecificLinks, dropdowns } = getSidebarLinks(role);

  // Determine title for role-specific section
  const getRoleSpecificTitle = () => {
    if (role === 'admin') return 'Painel Admin';
    if (role === 'mentor') return 'Painel Mentor';
    if (role === 'jogador') return 'Seu Painel';
    return '';
  };

  // Determine if we should show the profile section
  const showProfileSection = role && (role === 'admin' || role === 'mentor' || role === 'jogador');

  return (
    <div 
      className={`hidden md:block fixed top-0 bottom-0 bg-gradient-to-b from-[#181D24] to-[#1A2233] border-r border-[#222A35] shadow-xl transition-all duration-300 z-20 
        ${isCollapsed ? 'w-16' : 'w-64'}`}
      style={{ paddingTop: 'calc(64px + 16px)' }} // Height exata da navbar + padding
    >
      <div className="h-full flex flex-col">
        {/* Logo Section */}
        <div className={`flex items-center justify-center bg-[#181D24] border-b border-[#222A35]/30 relative ${isCollapsed ? 'h-12' : 'h-16'}`}>
          <Link 
            to="/"
            className="cursor-pointer hover:opacity-80 transition-opacity"
            title="Voltar para a página inicial"
          >
            <img 
              src={logo} 
              alt="Arena Pro Player Logo" 
              className={`object-contain transition-all duration-300 ${
                isCollapsed ? 'w-8 h-8' : 'w-12 h-12'
              }`}
            />
          </Link>
          
          {/* Botão toggle posicionado na seção do logo */}
          <button 
            onClick={toggleSidebar}
            className="absolute right-[-12px] top-1/2 transform -translate-y-1/2 bg-[#222A35] border border-[#2D3748] rounded-full w-6 h-6 flex items-center justify-center text-[#00B6F0] z-30"
          >
            {isCollapsed ? (
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            ) : (
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            )}
          </button>
        </div>

        {showProfileSection && (
          <SidebarUserProfile 
            collapsed={isCollapsed} 
            nickname={nickname} 
            role={role}
          />
        )}

        {/* Container scrollável para os links */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden sidebar-scroll">
          <SidebarLinks 
            links={roleSpecificLinks} 
            collapsed={isCollapsed} 
            title={getRoleSpecificTitle()}
          />
          
          {/* Renderizar dropdowns se existirem */}
          {dropdowns && dropdowns.length > 0 && (
            <div className="px-2 mb-4">
              {dropdowns.map((dropdown, index) => (
                <SidebarDropdown
                  key={index}
                  label={dropdown.label}
                  icon={dropdown.icon}
                  items={dropdown.items}
                  collapsed={isCollapsed}
                />
              ))}
            </div>
          )}
          
          <SidebarLinks 
            links={mainLinks} 
            collapsed={isCollapsed} 
            title="Navegação"
          />
        </div>
        
        <SidebarFooter collapsed={isCollapsed} />
      </div>
    </div>
  );
};

